import React from 'react';
import { DateTime } from 'luxon';

import { ALBUM_SUBTYPE } from '../../../../../../constants/albums';
import { EVENT_SETTING_ANNIVERSARY } from '../../../../../../constants/album-folder';

/**
 * Given a name, return the same name using it's possessive form.
 *
 * @param {String} name the name to parse
 * @returns {string} the parsed name
 */
export const getNameWithPossessive = name => {
  if (!name) {
    return '';
  }

  return `${name}${name.charAt(name.length - 1) === 's' ? "'" : "'s"}`;
};

/**
 * Given an albumSubType and the folder's eventSettings, retrieve the name of the celebrated person.
 *
 * @param {String} albumSubType the album subType
 * @param {Object} eventSettings the album folder event settings
 * @param {Boolean} withPosessive whether or not to retrieve the name with possessive
 * @param {String} defaultName a default name to return when no proper name is found
 *
 * @returns {string} the celebrated name
 */
export const getCelebratedPersonName = (
  albumSubType,
  eventSettings,
  withPosessive = false,
  defaultName = '',
) => {
  const possessiveDefaultName = getNameWithPossessive(defaultName);

  if (!albumSubType || !eventSettings) {
    return withPosessive ? possessiveDefaultName : defaultName;
  }

  if (
    albumSubType === ALBUM_SUBTYPE.WEDDINGS ||
    (albumSubType === ALBUM_SUBTYPE.ANNIVERSARIES &&
      eventSettings.anniversary_type === EVENT_SETTING_ANNIVERSARY.WEDDING)
  ) {
    if (!eventSettings.groom_name || !eventSettings.bride_name) {
      return withPosessive ? possessiveDefaultName : defaultName;
    }

    const groomName = withPosessive
      ? getNameWithPossessive(eventSettings.groom_name)
      : eventSettings.groom_name;

    return `${eventSettings.bride_name} & ${groomName}`;
  }

  if (albumSubType === ALBUM_SUBTYPE.FUNDRAISERS) {
    if (!eventSettings.charity_name) {
      return withPosessive ? possessiveDefaultName : defaultName;
    }

    return withPosessive
      ? getNameWithPossessive(eventSettings.charity_name)
      : eventSettings.charity_name;
  }

  if (!eventSettings.first_name) {
    return withPosessive ? possessiveDefaultName : defaultName;
  }

  return `${
    withPosessive
      ? getNameWithPossessive(eventSettings.first_name)
      : eventSettings.first_name
  }`;
};

export const getEventDateLabel = (albumFolder, format) => {
  switch (albumFolder.album_subtype) {
    case ALBUM_SUBTYPE.MEMORIALS: {
      if (!albumFolder?.event_settings?.date_of_birth) {
        return undefined;
      }

      const dateOfBirth = DateTime.fromISO(
        albumFolder?.event_settings?.date_of_birth,
      ).toLocaleString(format);

      const dateOfPassing = albumFolder?.event_settings?.date_of_passing
        ? DateTime.fromISO(
            albumFolder?.event_settings?.date_of_passing,
          ).toLocaleString(format)
        : '';

      return (
        <>
          {dateOfBirth}
          {!!dateOfPassing && <> &ndash; {dateOfPassing}</>}
        </>
      );
    }
    default: {
      const eventDate = albumFolder?.event_settings?.event_date;

      if (eventDate) {
        return DateTime.fromISO(eventDate).toLocaleString(
          format || DateTime.DATE_FULL,
        );
      }

      const startDate = albumFolder?.start_date;

      return startDate
        ? DateTime.fromJSDate(new Date(startDate)).toLocaleString(
            format || DateTime.DATE_FULL,
          )
        : undefined;
    }
  }
};
